import { useEffect, useRef } from 'react';
import { ts } from '@shared/lib/diagTs';
import { getRelayOwnership } from '@core/realtime/relayOwnership';
import { startOfDayUtcIso } from '@shared/lib/userTz';
import { useCqgImport } from './useCqgConnect';
import { useCqgDisplayState } from './useCqgDisplayState';

/**
 * Periodic "gap-fill" re-pull (2026-07-08 user request; re-cadenced + re-windowed 2026-09-14). While
 * CQG is LIVE, re-pull the trader's whole current business day every {@link RESYNC_INTERVAL_MS} so any
 * fill lost to a dropped relay frame (e.g. the BE briefly having no session — the "CqgInbound: no
 * session … dropping N bytes" gap) is backfilled without the user doing anything.
 *
 * **Why a whole business day and not a short lookback.** CQG's `HistoricalOrdersRequest.from_date` is a
 * BUSINESS DATE, not a timestamp, so the old 2-hour `from` already returned the whole business date —
 * the narrower window bought nothing CQG could act on. Asking for the day explicitly is honest about
 * what comes back, and {@link DAY_OVERLAP_DAYS} covers CQG's ~17:00 CT date roll while keeping the
 * instant in the past for a trader east of UTC.
 *
 * Safe + cheap because the re-pull is IDEMPOTENT server-side: `POST /cqg/import { from }` flows through
 * the normal ingest, which dedups on `external_id = {orderId}:{transId}` and only inserts the MISSING
 * fills, then does a scoped FIFO rebuild. `onSuccess` invalidates `['trades']` + `['statistics']`, so
 * the UI refreshes with whatever was backfilled.
 *
 * The cadence is ELAPSED-TIME based, not timer based (2026-09-15): the backend's idle watchdog reopens
 * the socket every 15 minutes, each reopen flips `isLive` and re-runs this effect, and a plain
 * 30-minute `setInterval` was restarted from zero every time and never fired. See {@link TICK_MS}.
 *
 * Cost guards, in order: only while live; only from the tab that OWNS the CQG socket (cross-tab
 * ownership, 2026-09-13 — otherwise every open tab re-pulls and CQG permits ONE historical request in
 * flight); never while the tab is hidden; single-flight on the mutation; and a
 * {@link MIN_RESYNC_GAP_MS} floor shared by BOTH triggers, so tab-flipping cannot turn a 30-minute poll
 * into a per-focus one. Mounted once in `AppShell`.
 */
const RESYNC_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes (user ruling, 2026-09-14)
/**
 * How often the elapsed-time check RUNS. It is not the cadence — {@link RESYNC_INTERVAL_MS} is.
 *
 * The cadence must not live in the timer itself. This effect re-runs on every `isLive` flip, and the
 * BACKEND's idle watchdog closes and reopens the browser socket every 15 minutes, which flips `live` →
 * `connecting` → `live` each time. A 30-minute `setInterval` was therefore torn down and restarted from
 * zero every 15 minutes and could never reach 30: dev 2026-09-15, a tab connected from 22:30 to 06:42
 * with the watchdog cycling at 02:43, 02:58, 03:14 and 03:29 produced ZERO interval polls. Checking
 * elapsed time against a ref that survives the re-arm makes the cadence immune to it.
 */
const TICK_MS = 60 * 1000;
/** Whole business dates to include BEFORE the trader's current one. 1 covers CQG's ~17:00 CT roll. */
const DAY_OVERLAP_DAYS = 1;
/** Floor between two re-pulls from ANY trigger — the refocus catch-up must not bypass the cadence. */
const MIN_RESYNC_GAP_MS = 5 * 60 * 1000;

export function useCqgGapFillResync(): void {
  const { display } = useCqgDisplayState();
  const isLive = display === 'live';
  const importCqg = useCqgImport();

  // Latest values read inside the interval/listener without re-arming them every render.
  const isPendingRef = useRef(importCqg.isPending);
  isPendingRef.current = importCqg.isPending;
  const mutateRef = useRef(importCqg.mutate);
  mutateRef.current = importCqg.mutate;
  /**
   * Epoch ms of the last re-pull THIS tab fired, shared by the interval and the refocus trigger, and —
   * critically — NOT reset when the effect re-arms. Seeded at the first live transition rather than 0 so
   * the first interval poll lands a full cadence later: the backend's connect-time auto-import has just
   * pulled history at logon, so an immediate re-pull would only duplicate it.
   */
  const lastResyncAtRef = useRef(0);

  useEffect(() => {
    if (!isLive) return;
    if (lastResyncAtRef.current === 0) lastResyncAtRef.current = Date.now();

    const resync = (why: string) => {
      if (typeof document !== 'undefined' && document.hidden) return; // don't spend while backgrounded
      if (isPendingRef.current) return;                               // single-flight
      // Another tab owns the CQG socket: it is the one relaying frames, and it runs this same poll.
      // `isPassive()` fails OPEN for a lone tab, which has never had to claim anything.
      if (getRelayOwnership('cqg').isPassive()) return;
      const now = Date.now();
      if (now - lastResyncAtRef.current < MIN_RESYNC_GAP_MS) return;
      lastResyncAtRef.current = now;
      const from = startOfDayUtcIso(DAY_OVERLAP_DAYS, new Date(now));
      console.log('%s [cqg-gapfill] re-pull the business day from %s (%s)', ts(), from, why);
      mutateRef.current({ from });
    };

    const interval = setInterval(() => {
      if (Date.now() - lastResyncAtRef.current >= RESYNC_INTERVAL_MS) resync('interval');
    }, TICK_MS);
    // Catch-up: a tab that was hidden across one or more ticks reconciles the moment it regains focus.
    const onVisible = () => { if (!document.hidden) resync('tab-visible'); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLive]);
}
